/* heirloom.js — ported from build/script.js per build/CONTRACT.md §12.
   Behaviour is unchanged except for: variant selection, prices, the ATC label,
   /cart/add.js, and hiding the wallets when a bump is checked.
   Every shared let/var is declared at the top (trap 4 / lesson 10). */
(function () {

  /* ---------- shared state: declared at the top, before any module-level use ---------- */
  var HH = null,
      MEDIA = [],
      HERO = { christmas: 0, halloween: 4 },
      stage = null, trail = null, dots = null, gi = 0,
      tiers = [], eds = [], edSwaps = [], bumps = [],
      atc = null, sub = null, walnote = null, walnoteOrig = "", wallets = null,
      form = null, hiddenId = null,
      lastEd = null, busy = false, noteMsg = null,
      obs = null,
      mqFill = null, mqTimer = null, mqResizeBound = false,
      /* ---- cart drawer. null means NOT YET FETCHED and must never be read as "empty". ---- */
      CART = null, cartBusy = false, cartErr = null, drawerBound = false, bumpFixed = {};

  var FAILNOTE = "That didn't go through. Nothing was added — please try again.";
  /* Christmas in a Bottle stepper (2026-10-21), see bottleStepHTML(): the line-item property a 2- or 3-bottle line carries, and
     the two messages a swap can end with. */
  var BOT_PROP_K = "Shipping protection", BOT_PROP_V = "Included free",
      BOT_FAIL = "We couldn\u2019t update your cart. Please try again.",
      BOT_MOVED = "Prices just updated \u2014 refreshing the page.";

  /* ---------- helpers ---------- */
  function money(cents) { return "$" + (cents / 100).toFixed(2); }
  function norm(x) { return String(x == null ? "" : x).trim().toLowerCase(); }
  function key(ed, pk) { return norm(ed) + "|" + norm(pk); }
  function lookup(ed, pk) { return (HH && HH.variants) ? HH.variants[key(ed, pk)] : null; }

  /* PRESEASON SALE: variant id -> HH variant (price + compare_at), so the cart can price the
     markdown. Built lazily on first use, by which point HH is populated. */
  var KIT_BY_ID = null;
  function kitById(id) {
    if (!KIT_BY_ID) {
      KIT_BY_ID = {};
      /* §14: HH.kits covers BOTH kit products, so a cart holding the other product's variants still
         prices correctly on this page. HH.variants (this product) is layered on top. */
      if (HH && HH.kits) Object.keys(HH.kits).forEach(function (id) {
        var kk = HH.kits[id];
        if (kk && kk.id) KIT_BY_ID[String(kk.id)] = kk;
      });
      if (HH && HH.variants) Object.keys(HH.variants).forEach(function (k) {
        var kv = HH.variants[k];
        if (kv && kv.id) KIT_BY_ID[String(kv.id)] = kv;
      });
    }
    return KIT_BY_ID[String(id)] || null;
  }
  /* The markdown saving is (compare_at - price) x quantity, per kit line. Shopify's
     CART.total_discount only counts discount CODES, so on its own the drawer's savings row
     stayed hidden and the sale never appeared in the cart at all. Add-on lines are not in
     HH.variants and correctly contribute nothing. */
  /* Largest markdown across the kit variants, FLOORED — identical maths to
     snippets/hh-max-off.liquid, so the cart can never quote a bigger % than the banner. */
  function maxOffPct() {
    var m = 0;
    if (HH && HH.variants) Object.keys(HH.variants).forEach(function (k) {
      var kv = HH.variants[k];
      if (kv && isFinite(kv.compare_at) && kv.compare_at > kv.price)
        m = Math.max(m, Math.floor((kv.compare_at - kv.price) * 100 / kv.compare_at));
    });
    return m;
  }
  function markdownSavings(c) {
    var s = 0;
    ((c && c.items) || []).forEach(function (it) {
      var kv = kitById(it.variant_id);
      if (kv && isFinite(kv.compare_at) && kv.compare_at > kv.price)
        s += (kv.compare_at - kv.price) * (Number(it.quantity) || 0);
    });
    return s;
  }
  function bump(tag) { return (HH && HH.bumps) ? HH.bumps[tag] : null; }
  function valid(v) {
    return !!v && !!v.id && v.available !== false &&
           isFinite(v.price) && v.price >= 0 &&
           isFinite(v.qty) && v.qty > 0;
  }
  function bindable(el) {
    if (!el || el.getAttribute("data-hh-bound")) return false;
    el.setAttribute("data-hh-bound", "1");
    return true;
  }

  /* ================= marquee — unchanged from script.js ================= */
  function initMarquee() {
    var track = document.querySelector(".mq__track");
    if (!track || !track.firstElementChild) return;
    var proto = track.firstElementChild.cloneNode(true), SPEED = 47;   /* px/sec, matches gf-v6 feel */
    if (window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches) {
      var mq = document.querySelector(".mq");
      if (mq) mq.classList.add("mq--static");
      mqFill = null;
      return;
    }
    mqFill = function fill() {
      track.style.animation = "none";
      track.innerHTML = "";
      track.appendChild(proto.cloneNode(true));
      var w = track.firstElementChild.getBoundingClientRect().width;
      if (!w) return;
      var half = Math.max(1, Math.ceil(window.innerWidth / w));
      for (var i = 1; i < half * 2; i++) track.appendChild(proto.cloneNode(true));
      void track.offsetWidth;
      track.style.animation = "mq " + ((half * w) / SPEED).toFixed(2) + "s linear infinite";
    };
    mqFill();
    if (!mqResizeBound) {
      mqResizeBound = true;
      window.addEventListener("resize", function () {
        clearTimeout(mqTimer);
        mqTimer = setTimeout(function () { if (mqFill) mqFill(); }, 150);
      });
    }
  }

  /* ================= gallery ================= */
  /* §14 GALLERY BY EDITION: every HH.media item carries "e". Each edition shows only its own
     photos. Fallbacks so the gallery can never be empty: Halloween -> the legacy single Halloween
     photo -> the Christmas set; anything untagged counts as Christmas. strict=true skips the
     cross-edition fallbacks (used for the cart cross-sell thumbnail). */
  var galleryEd = null;
  function mediaFor(ed, strict) {
    var all = MEDIA || [];
    var pick = function (tag) { return all.filter(function (m) { return (m.e || "christmas") === tag; }); };
    var list = pick(ed);
    if (list.length) return list;
    if (ed === "halloween") { list = pick("halloween-legacy"); if (list.length) return list; }
    if (strict) return [];
    list = pick("christmas");
    return list.length ? list : all;
  }
  function media() { return mediaFor(galleryEd || edition()); }

  function buildGallery() {
    galleryEd = edition();
    gi = 0;
    [].slice.call(stage.querySelectorAll("img,video")).forEach(function (el) { el.remove() });
    dots.innerHTML = ""; trail.innerHTML = "";
    var list = media();
    list.forEach(function (m, i) {
      var el;
      if (m.k === "v") {
        el = document.createElement("video");
        el.src = m.s; el.poster = m.p; el.muted = true; el.loop = true; el.playsInline = true; el.preload = "none";
      } else {
        el = document.createElement("img"); el.src = m.s; el.alt = m.a; el.loading = i ? "lazy" : "eager";
      }
      stage.appendChild(el);

      var d = document.createElement("button"); d.type = "button";
      d.setAttribute("aria-label", "Go to image " + (i + 1));
      d.addEventListener("click", function () { go(i) }); dots.appendChild(d);

      var b = document.createElement("button"); b.type = "button";
      b.setAttribute("aria-label", m.a);
      var im = document.createElement("img");
      im.src = m.t || (m.k === "v" ? m.p : m.s); im.alt = ""; im.loading = "lazy";
      b.appendChild(im);
      if (m.k === "v") { var pl = document.createElement("span"); pl.className = "play"; pl.textContent = "▶"; b.appendChild(pl); }
      b.addEventListener("click", function () { go(i) }); trail.appendChild(b);
    });
    var solo = list.length < 2;
    trail.classList.toggle("solo", solo);
    dots.style.display = solo ? "none" : "";
    document.getElementById("gPrev").style.display = solo ? "none" : "";
    document.getElementById("gNext").style.display = solo ? "none" : "";
    go(0);
  }

  function go(i) {
    var list = media();
    if (!list.length) return;
    gi = (i + list.length) % list.length;
    [].slice.call(stage.querySelectorAll("img,video")).forEach(function (el, k) {
      var on = k === gi; el.classList.toggle("on", on);
      if (el.tagName === "VIDEO") { if (on) { el.preload = "auto"; el.play().catch(function () { }); } else el.pause(); }
    });
    [].slice.call(trail.children).forEach(function (b, k) { b.setAttribute("aria-current", k === gi ? "true" : "false") });
    [].slice.call(dots.children).forEach(function (b, k) { b.setAttribute("aria-current", k === gi ? "true" : "false") });
  }

  /* ================= edition ================= */
  function edition() {
    var on = eds.filter(function (e) { return e.querySelector("input").checked })[0];
    return on ? on.querySelector("input").value : "christmas";
  }

  function paintEdition() {
    var cur = edition();
    eds.forEach(function (e) { e.classList.toggle("on", e.querySelector("input").value === cur) });
    edSwaps.forEach(function (el) { el.hidden = el.dataset.ed !== cur });
    if (cur !== lastEd) {
      lastEd = cur;
      /* §14: each edition has its own photo set — rebuild from its first photo on change.
         (HERO is no longer used to jump; it is kept only as harmless legacy state.) */
      if (stage && cur !== galleryEd) buildGallery();
    }
  }

  function picked() { return bumps.filter(function (b) { return b.querySelector("input").checked }) }
  function selectedTier() { return tiers.filter(function (t) { return t.querySelector("input").checked })[0] }

  /* ================= price rendering ================= */
  function paintTier(t, v) {
    if (!valid(v)) return;                       /* leave Liquid's server render in place */
    var now = t.querySelector(".tnow"),
        was = t.querySelector(".twas"),
        each = t.querySelector(".teach");
    if (now) now.textContent = money(v.price);
    if (each) each.textContent = money(Math.round(v.price / v.qty)) + " each";
    if (was) {
      if (isFinite(v.compare_at) && v.compare_at > v.price) {
        was.textContent = money(v.compare_at);
        was.hidden = false;
      } else {
        was.hidden = true;                       /* never an empty strikethrough */
      }
    }
    var off = t.querySelector(".hhoff");
    if (off) {
      /* Same floor maths as the Liquid render and snippets/hh-max-off.liquid: integer cents over
         an integer, so an exact 50% stays 50 and 52.78% can only ever read as 52. U+2212 minus,
         matching the &minus; Liquid renders, so the first repaint never visibly changes it. */
      var pct = (isFinite(v.compare_at) && v.compare_at > v.price)
        ? Math.floor((v.compare_at - v.price) * 100 / v.compare_at) : 0;
      if (pct > 0) { off.textContent = "\u2212" + pct + "%"; off.hidden = false; }
      else off.hidden = true;                    /* never a "-0%" chip */
    }
  }

  function setEnabled(on) {
    if (!atc) return;
    var live = on && !busy;
    if (live) {
      atc.removeAttribute("disabled");
      atc.removeAttribute("aria-disabled");
    } else {
      atc.setAttribute("disabled", "");
      atc.setAttribute("aria-disabled", "true");
    }
    /* mirror onto the secondary add buttons (sticky bar + "Get the kit"), resolved fresh —
       they live in the other section and a section reload replaces those nodes. addToCart()
       already refuses to run while #atc is disabled; this just stops that being invisible. */
    [].slice.call(document.querySelectorAll("[data-hh-add]")).forEach(function (b) {
      if (live) { b.removeAttribute("disabled"); b.removeAttribute("aria-disabled"); }
      else { b.setAttribute("disabled", ""); b.setAttribute("aria-disabled", "true"); }
    });
    /* never touch textContent on #atc — it destroys the <span>/<i> (trap 3) */
  }

  function paintWallets(anyBump) {
    /* Previously this hid #wallets whenever an add-on was ticked, because Shopify's dynamic
       checkout button bypasses the cart and would have silently dropped it. The express buttons
       are now our own and post the add-ons with the kit, so there is nothing to hide from and
       nothing to drop. Kept as a no-op hook so the call sites stay honest. */
    if (!wallets) return;
    wallets.hidden = false;
    if (walnote && walnoteOrig) walnote.textContent = walnoteOrig;
  }

  function sync() {
    paintEdition();
    var ed = edition(),
        add = picked(),
        tags = add.map(function (b) { return b.dataset.tag }).join(" + "),
        edName = ed === "halloween" ? "Halloween" : "Christmas",
        sel = selectedTier(),
        v = sel ? lookup(ed, sel.dataset.name) : null,
        extra = 0,
        ok = valid(v);

    add.forEach(function (b) {
      var bp = bump(b.dataset.tag);
      if (bp && bp.id && isFinite(bp.price)) extra += bp.price; else ok = false;
    });

    tiers.forEach(function (t) {
      t.classList.toggle("on", t.querySelector("input").checked);
      paintTier(t, lookup(ed, t.dataset.name));
    });

    bumps.forEach(function (b) {
      var bp = bump(b.dataset.tag), el = b.querySelector(".bumpprice");
      if (el && bp && isFinite(bp.price)) el.textContent = "+" + money(bp.price);
      b.classList.toggle("on", b.querySelector("input").checked);
    });

    if (ok) {
      var total = v.price + extra;
      if (hiddenId) hiddenId.value = v.id;
      if (atc) atc.innerHTML = '<i class="ms">shopping_bag</i> Add to cart — ' + money(total);
      if (sub) sub.textContent = edName + " · " + sel.dataset.name + (tags ? " + " + tags : "") + " — " + money(total);
    } else if (hiddenId) {
      hiddenId.value = "";
    }

    paintWallets(add.length > 0);
    setEnabled(ok);
  }

  /* any shopper interaction with the offer clears a latched failure message */
  function onChange() { noteMsg = null; sync(); }

  /* ================= add to cart ================= */
  function post(url, payload) {
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(payload)
    });
  }

  function get(url) {
    return fetch(url, { credentials: "same-origin", headers: { "Accept": "application/json" } });
  }

  /* Liquid's routes.cart_*_url are EXTENSIONLESS. The extensionless route serves money as
     dollar STRINGS ("39.99") where every .js route serves cents (3999) — parse one as the
     other and the drawer prints $0.40 for a $39.99 line. The Liquid now appends .js; this is
     the belt for the fallbacks and for an older cached HH. */
  function api(u, fb) { u = u || fb; return /\.js(\?|$)/.test(u) ? u : u + ".js"; }

  function addToCart(toCheckout) {
    if (!atc || atc.hasAttribute("disabled") || busy) return;
    var ed = edition(),
        sel = selectedTier(),
        v = sel ? lookup(ed, sel.dataset.name) : null;
    if (!valid(v)) { setEnabled(false); return; }

    var bad = false;
    picked().forEach(function (b) {
      var bp = bump(b.dataset.tag);
      if (!bp || !bp.id) bad = true;
    });
    if (bad) { setEnabled(false); return; }

    /* Built AFTER the (express-only) fresh cart read below, because both dedup rules read CART. */
    function buildItems() {
      var items = [];
      /* §15 EXPRESS BUTTONS (2026-09-13 fix). An express click (Shop Pay / Apple Pay / Google
         Pay / PayPal) means "check out with this", never "add another one". It used to add the
         selected pack unconditionally, so a shopper who had ALREADY added it to cart reached
         checkout with it twice — verified live: $64.99 in cart -> $129.98 at checkout. So an
         express click skips the kit when the cart already holds that exact variant. The main
         Add to cart button is unchanged and stays additive on purpose. */
      if (!(toCheckout && inCart(v.id))) items.push({ id: v.id, quantity: 1 });
      picked().forEach(function (b) {
        var bp = bump(b.dataset.tag);
        /* /cart/add.js MERGES an identical variant by incrementing its quantity. The kit may
           legitimately be added twice; a per-order bump may not — two shipping protections on
           one order is a refund conversation. Drop any bump the server already holds. */
        if (inCart(bp.id)) return;
        items.push({ id: bp.id, quantity: 1 });
      });
      return items;
    }

    var R = (HH && HH.routes) || {};
    noteMsg = null;                              /* a fresh attempt clears the previous failure */
    busy = true;
    setBusyAttr(true);

    function done() {
      busy = false;
      setBusyAttr(false);
      sync();                                    /* re-derives the enabled state */
    }

    /* No /cart/clear.js any more. It existed because the cart was defined to mirror the buy
       box; with a drawer that offers +/-/remove, a clear would silently throw away the
       shopper's own edits and delete a kit they added on a previous visit. The add is now
       additive, so #atc's label means "what this button adds" and the drawer's subtotal —
       read from the server — means "what is in the cart". */
    /* Express reads the cart FRESH first: CART can be stale (another tab) or still null (not yet
       fetched). If that read fails, fall back to whatever CART holds — the old behaviour. */
    var ready = toCheckout ? refreshCart().catch(function () { return null; }) : Promise.resolve(null);
    ready.then(function () {
      var items = buildItems();
      if (toCheckout && !items.length) {
        /* everything the button quotes is already in the cart: go straight to checkout */
        location.href = (HH && HH.routes && HH.routes.checkout) || "/checkout";
        return;
      }
      return post(api(R.add, "/cart/add.js"), { items: items })
      .then(function (res) {
        if (res.ok) {
          /* add.js returns only the lines it touched and NO totals, so the drawer cannot be
             rendered from it. Re-read the whole cart and render that. */
          return refreshCart().then(function () {
            cartErr = null;
            fixBumpQty();
            /* Express buttons skip the drawer entirely and go to checkout, which is the whole
               point of them. The add has landed and the cart has been re-read, so checkout
               receives exactly what the button quoted. */
            if (toCheckout) {
              location.href = (HH && HH.routes && HH.routes.checkout) || "/checkout";
              return;
            }
            openCart();
          }, function () {
            /* the add DID land; only the read failed. Open on the retry state (CART stays
               null) rather than pretending nothing happened. */
            cartErr = "We couldn't load your cart just then.";
            /* the add DID land, so an express click still belongs at checkout */
            if (toCheckout) {
              location.href = (HH && HH.routes && HH.routes.checkout) || "/checkout";
              return;
            }
            openCart();
          });
        }
        /* the add is atomic — one bad line rejects all of them, so nothing was added and the
           drawer must NOT open onto a cart that does not contain what the button quoted. */
        return res.json().catch(function () { return null; }).then(function (j) {
          noteMsg = (j && (j.description || j.message)) || FAILNOTE;
          throw new Error("add failed");
        });
      });
    })
      .then(done, function () {                  /* network/parse failures get the fallback too */
        if (!noteMsg) noteMsg = FAILNOTE;
        done();
      });
  }

  /* busy latch, mirrored onto the secondary buttons. innerHTML is never touched here —
     setting textContent on #atc destroys the <i class="ms"> inside it and every later
     render throws (trap 3). */
  function setBusyAttr(on) {
    var els = [].slice.call(document.querySelectorAll("[data-hh-add]"));
    if (atc) els.push(atc);
    els.forEach(function (b) {
      if (on) {
        b.setAttribute("disabled", "");
        b.setAttribute("aria-disabled", "true");
        b.setAttribute("data-hh-busy", "1");
      } else {
        b.removeAttribute("data-hh-busy");
      }
    });
  }

  /* ================= cart drawer =================
     Server-authoritative, always. Every number rendered below comes out of /cart.js or out of
     the cart object /cart/change.js returns. Nothing here is summed, computed or optimistic:
     the local-state drawer is what produced GripForge's worst bug — an add-on that ticked ON,
     charged nothing, and left the shopper believing they had bought it.

     window.HH stays the source of truth for the BUY BOX ("what you are about to add").
     /cart.js is the source of truth for the DRAWER ("what Shopify actually holds").
     The two are never reconciled in JavaScript.

     Scroll lock: there is none, deliberately. ANY overflow value on <body> makes it a scroll
     container and silently kills position:sticky on .gallery AND on header.site (which is
     sticky at every width, so even a mobile-only lock would break it). The scrolling list
     carries overscroll-behavior:contain instead, so a scroll inside the drawer does not chain
     to the page. Do not "fix" this by touching body. */

  function el(id) { return document.getElementById(id); }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  /* item.image from /cart.js is the product's FULL-SIZE featured image — 3712x4608 here.
     Poured straight into a 56px box that is ~2.2MB a line and the drawer reads as empty
     until it downloads. Strip any width/height the URL carries and ask for our own. */
  function thumb(u, w) {
    if (!u) return "";
    var parts = String(u).split("#")[0].split("?"),
        q = (parts[1] || "").split("&").filter(function (p) { return p && !/^(width|height)=/i.test(p); });
    q.push("width=" + w);
    return parts[0] + "?" + q.join("&");
  }

  /* The two add-on products carry no product image, so Shopify hands back a generic gift
     placeholder for their cart lines. Reuse the same SVG the ADD TO YOUR ORDER row uses, so a
     line and its upsell row are visibly the same thing. */
  function bumpTagFor(id) {
    var b = (HH && HH.bumps) || null, k;
    if (!b) return null;
    for (k in b) if (b[k] && Number(b[k].id) === Number(id)) return k;
    return null;
  }

  function bumpIcon(id) {
    var tag = bumpTagFor(id);
    /* UPSELLS is assigned further down the file. bumpIcon only ever runs at render time, well
       after init, so it is populated by then -- but guard anyway rather than depend on call
       order. A missing icon must degrade to the placeholder, never throw and blank the drawer. */
    if (!tag || typeof UPSELLS === "undefined" || !UPSELLS) return "";
    for (var i = 0; i < UPSELLS.length; i++) if (UPSELLS[i].tag === tag) return UPSELLS[i].icon;
    return "";
  }

  function isBumpVariant(id) {
    var b = (HH && HH.bumps) || null, k;
    if (!b) return false;
    for (k in b) if (b[k] && Number(b[k].id) === Number(id)) return true;
    return false;
  }

  function inCart(id) {
    if (!CART || !CART.items) return false;
    for (var i = 0; i < CART.items.length; i++) {
      if (Number(CART.items[i].variant_id) === Number(id)) return true;
    }
    return false;
  }

  function refreshCart() {
    var R = (HH && HH.routes) || {};
    return get(api(R.cartjs, "/cart.js")).then(function (res) {
      if (!res.ok) throw new Error("cart read failed");
      return res.json();
    }).then(function (c) { CART = c; return c; });
  }

  /* The Prism Tree (2026-10-07). Its sets are variants priced by figure count, and a set of 4+ is The Mantel Set + Extra-figure
     lines — so a Prism line must NEVER get the stepper (3 x Centerpiece would bill $269.97 where the page sells 3 figures for
     $199.99). Prism lines get one Remove, which takes the whole set out (set + extras) so an extra can't be left at $66.66. */
  function isPrismItem(it) { return String((it && it.sku) || "").indexOf("HH-PRISM") === 0; }
  function isPrismExtra(it) { return String((it && it.sku) || "").indexOf("HH-PRISM-X") === 0; }
  /* Christmas in a Bottle (2026-10-20): a bottle line is one tier (The Single / The Pair / The Family Set), so it never gets the
     quantity stepper (2 x The Single would bill more than The Pair).
     2026-10-21 (Julian: "up and down quantity and delete, just like the Heirloom Tree kit"): it gets the kit stepper's LOOK, but the
     number is BOTTLES and +/- swap the line's variant: 1 The Single, 2 The Pair, 3 The Family Set. Plus is disabled at three; at one
     bottle the minus is the delete icon. The tiers come from HH.bottle (sections/heirloom-buybox -> snippets/hh-bottle-json); without
     it (bottle not live, an old cached page) the line keeps its one Remove. */
  function isBottleItem(it) { return String((it && it.sku) || "").indexOf("HH-BOTTLE") === 0; }
  function bottleTiers() { return (HH && HH.bottle && HH.bottle.tiers) || null; }
  function bottleTierOf(it) { var ts = bottleTiers(); return (ts && it && ts[String(it.variant_id)]) || null; }
  function bottleByN(n) {
    var ts = bottleTiers(), k;
    if (ts) for (k in ts) if (ts[k] && ts[k].id && Number(ts[k].n) === Number(n)) return ts[k];
    return null;
  }
  function bottlesWord(n) { return n + (Number(n) === 1 ? " bottle" : " bottles"); }
  /* a sold-out tier is skipped, so a button never offers what Shopify would refuse; with none below, the minus deletes */
  function bottleUp(n) { for (var j = n + 1; j <= 3; j++) { var t = bottleByN(j); if (t && t.available !== false) return t; } return null; }
  function bottleDown(n) { for (var j = n - 1; j >= 1; j--) { var t = bottleByN(j); if (t && t.available !== false) return t; } return null; }
  function bottleStepHTML(it) {
    var cur = bottleTierOf(it), n = cur ? Number(cur.n) || 0 : 0;
    if (!n) return "";
    var up = bottleUp(n), dn = bottleDown(n), nx = bottleByN(n + 1), off = cartBusy ? " disabled" : "";
    var minus = dn
      ? '<button class="hhci__b" type="button" data-hh-bstep="' + dn.n + '" data-dir="down" aria-label="Fewer bottles: switch to ' +
          esc(dn.name || bottlesWord(dn.n)) + ', ' + bottlesWord(dn.n) + '"' + off + '>&minus;</button>'
      : '<button class="hhci__b" type="button" data-hh-q="0" data-dir="down" aria-label="' +
          (n === 1 ? "Remove your bottle" : "Remove your bottles") + '"' + off + '><i class="ms">delete</i></button>';
    var plus = up
      ? '<button class="hhci__b" type="button" data-hh-bstep="' + up.n + '" data-dir="up" aria-label="More bottles: switch to ' +
          esc(up.name || bottlesWord(up.n)) + ', ' + bottlesWord(up.n) + '"' + off + '>+</button>'
      : '<button class="hhci__b" type="button" data-dir="up" disabled aria-label="' +
          (n >= 3 || !nx ? "Three bottles is the biggest set" : esc(nx.name || bottlesWord(nx.n)) + " is sold out right now") + '">+</button>';
    return '<span class="hhci__step" data-hh-bottle>' + minus +
             '<input class="hhci__n" type="text" value="' + n + '" readonly tabindex="-1" aria-label="Bottles">' + plus + '</span>';
  }
  function hasFreeProp(it) { return !!(it && it.properties && it.properties[BOT_PROP_K] === BOT_PROP_V); }
  /* The swap, the bottle page's own write (bottle.js syncSet): on a FRESH cart, add the new tier first (with the "Shipping
     protection: Included free" property for 2 or 3 bottles), then ONE /cart/update.js clearing every other bottle line and, for 2
     or 3 bottles, a paid protection line. If the add is refused nothing has changed. Then re-read: exactly one bottle line of that
     tier at quantity 1, billed at the price this page was given, or it says so (and the caller reloads on a price change). */
  function swapBottle(n) {
    var t = bottleByN(n), R = (HH && HH.routes) || {}, free = Number(n) >= 2;
    if (!t) return Promise.reject(new Error(BOT_FAIL));
    function okj(res) {
      if (res.ok) return res.json();
      return res.json().catch(function () { return null; }).then(function (j) { throw new Error((j && (j.description || j.message)) || BOT_FAIL); });
    }
    return refreshCart().then(function () {
      var have = CART.items.filter(isBottleItem);
      var keep = have.filter(function (it) { return Number(it.variant_id) === Number(t.id) && hasFreeProp(it) === free; })[0] || null;
      var upd = {}, need = false;
      have.forEach(function (it) { if (it !== keep) { upd[it.key] = 0; need = true; } });
      if (keep && Number(keep.quantity) !== 1) { upd[keep.key] = 1; need = true; }
      if (free) CART.items.forEach(function (it) { if (bumpTagFor(it.variant_id) === "protection") { upd[it.key] = 0; need = true; } });
      var item = { id: Number(t.id), quantity: 1 };
      if (free) { item.properties = {}; item.properties[BOT_PROP_K] = BOT_PROP_V; }
      var step = keep ? Promise.resolve() : post(api(R.add, "/cart/add.js"), { items: [item] }).then(okj);
      return step.then(function () { return need ? post(api(R.update, "/cart/update.js"), { updates: upd }).then(okj) : null; }).then(refreshCart);
    }).then(function () {
      var b = CART.items.filter(isBottleItem);
      if (b.length !== 1 || Number(b[0].variant_id) !== Number(t.id) || Number(b[0].quantity) !== 1 || hasFreeProp(b[0]) !== free) throw new Error(BOT_FAIL);
      if (Number(b[0].original_line_price) !== Number(t.price)) { var er = new Error(BOT_MOVED); er.moved = true; throw er; }
    });
  }
  function stepBottle(n, dir) {
    if (cartBusy || !bottleByN(n)) return;
    cartBusy = true; cartErr = null; renderCart();    /* one write in flight: the stepper is drawn disabled, a double click is one swap */
    var moved = false;
    swapBottle(n).then(function () { cartErr = null; }, function (e) {
      cartErr = (e && e.message) || BOT_FAIL;
      if (e && e.moved) { moved = true; return; }
      return refreshCart().catch(function () { });  /* never keep showing a cart the server did not end up with */
    }).then(function () {
      cartBusy = false; renderCart();
      if (moved) { setTimeout(function () { location.reload(); }, 1200); return; }
      var body = el("hh-lines"), st = body && body.querySelector("[data-hh-bottle]");   /* the line was re-drawn under a new key */
      var b = (st && (st.querySelector('[data-dir="' + dir + '"]:not([disabled])') || st.querySelector(".hhci__b:not([disabled])"))) || el("hh-close");
      if (b) { try { b.focus({ preventScroll: true }); } catch (e) { } }
    });
  }
  /* A bottle line is ONE line at quantity 1. A cart holding more (quantity 2 from an old page, or two bottle lines after a swap
     whose clearing step failed) becomes the tier for all its bottles, capped at three (Single x2 -> The Pair, Pair x2 -> The
     Family Set). Silent, on load only; never more bottles than the cart held, never a higher bill. */
  function normalizeBottles() {
    if (!CART || !CART.items || !bottleTiers()) return;
    var b = CART.items.filter(isBottleItem), n = 0, known = true;
    if (!b.length || (b.length === 1 && Number(b[0].quantity) === 1)) return;
    b.forEach(function (it) { var t = bottleTierOf(it); if (t) n += Number(t.n) * (Number(it.quantity) || 0); else known = false; });
    var to = bottleByN(Math.min(3, n));
    if (!known || !to || to.available === false) return;
    cartBusy = true;
    return swapBottle(to.n).catch(function () { return refreshCart().catch(function () { }); }).then(function () { cartBusy = false; });
  }
  /* 2 or 3 bottles: the shipping protection is part of the order at no charge -- shown as its own row, as on the bottle page and /cart */
  function freeProtRowHTML() {
    var b = (CART && CART.items || []).filter(function (it) { return /^HH-BOTTLE-[23]/.test(String(it.sku || "")); })[0];
    if (!b) return "";
    var t = bottleTierOf(b), name = (t && t.name) || ((b.variant_title && b.variant_title !== "Default Title") ? b.variant_title : ""),
        pp = bump("protection");
    return '<div class="hhci" data-hh-free-prot>' +
             '<div class="hhci__im is-icon">' + ICON_SHIELD + '</div>' +
             '<div class="hhci__d"><div class="hhci__t">Shipping protection</div>' +
               '<div class="hhci__v">Included free' + (name ? ' with ' + esc(name) : '') + '</div></div>' +
             '<div class="hhci__p">' + (pp && pp.price ? '<s>' + esc(money(pp.price)) + '</s>' : '') + 'Free</div>' +
           '</div>';
  }
  /* Port audit 2026-10-20: The Pair (HH-BOTTLE-2) and The Family Set (HH-BOTTLE-3) include shipping protection free, so this
     drawer never offers the paid one next to them and takes a paid one out (same rule as the bottle page and /cart). The Lifetime
     warranty (kit wording) is not offered on a cart that holds only bottles. */
  function freeBottleInCart() {
    return !!(CART && CART.items && CART.items.some(function (it) { return /^HH-BOTTLE-[23]/.test(String(it.sku || "")); }));
  }
  function onlyBottlesInCart() {
    if (!CART || !CART.items || !CART.items.some(isBottleItem)) return false;
    return CART.items.every(function (it) { return isBottleItem(it) || isBumpVariant(it.variant_id); });
  }

  function rowHTML(it) {
    var qty = Number(it.quantity) || 0,
        isB = isBumpVariant(it.variant_id),
        isP = isPrismItem(it),
        img = thumb(it.image, 120),
        vt = (it.variant_title && it.variant_title !== "Default Title") ? it.variant_title : "",
        was = (Number(it.original_line_price) > Number(it.final_line_price))
                ? "<s>" + esc(money(it.original_line_price)) + "</s>" : "",   /* never an empty strikethrough */
        ctl;

    if (!was) {
      /* no discount code on this line — show the preseason markdown instead */
      var kvw = kitById(it.variant_id);
      if (kvw && isFinite(kvw.compare_at) && kvw.compare_at > kvw.price)
        was = "<s>" + esc(money(kvw.compare_at * qty)) + "</s>";
    }

    if (isP) {
      var hasSet = !!(CART && CART.items && CART.items.some(function (i) { return isPrismItem(i) && !isPrismExtra(i); }));
      ctl = (isPrismExtra(it) && hasSet) ? '<span class="hhci__v">Part of your Prism Tree set</span>'
                                         : '<button class="hhci__rm" type="button" data-hh-prism-rm>Remove</button>';
    } else if (isBottleItem(it)) {
      ctl = bottleStepHTML(it) || '<button class="hhci__rm" type="button" data-hh-q="0" aria-label="Remove your bottles">Remove</button>';
    } else if (isB) {
      /* per-order add-ons get no stepper — quantity 2 is a refund conversation, not a choice */
      ctl = '<button class="hhci__rm" type="button" data-hh-q="0">Remove</button>';
    } else {
      /* Plain quantity is safe HERE and only here, because the packs are real Shopify
         variants: quantity 2 on a "Two kits" line is billed as exactly 2 x $64.99 by
         Shopify. GripForge could not do this — its stepper tripped a bundle app and leaked
         $219.99 on one line. IF A BUNDLE OR DISCOUNT APP IS EVER INSTALLED ON THIS STORE,
         THIS STEPPER MUST GO BACK TO SWAPPING VARIANTS. */
      ctl = '<span class="hhci__step">' +
              '<button class="hhci__b" type="button" data-hh-q="' + (qty - 1) + '" aria-label="' +
                (qty > 1 ? "Decrease quantity" : "Remove this item") + '">' +
                (qty > 1 ? "&minus;" : '<i class="ms">delete</i>') +
              '</button>' +
              '<input class="hhci__n" type="number" inputmode="numeric" min="1" max="10" value="' +
                qty + '" data-hh-qty aria-label="Quantity">' +
              '<button class="hhci__b" type="button" data-hh-q="' + (qty + 1) + '" aria-label="Increase quantity"' +
                (qty >= 10 ? " disabled" : "") + '>+</button>' +
            '</span>';
    }

    /* data-hh-key is the LINE KEY, never a position. /cart.js does not return lines in the
       order they were added, and removing a line renumbers every line after it — an index
       would cheerfully delete the wrong one and answer HTTP 200. */
    return '<div class="hhci" data-hh-key="' + esc(it.key) + '">' +
             '<div class="hhci__im' + (isB ? ' is-icon' : '') + '">' +
               ((isB && bumpIcon(it.variant_id))
                 || (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '<i class="ms">redeem</i>')) +
             '</div>' +
             '<div class="hhci__d">' +
               '<div class="hhci__t">' + esc(it.product_title) + '</div>' +
               (vt ? '<div class="hhci__v">' + esc(vt) + '</div>' : '') +
               '<div class="hhci__c">' + ctl + '</div>' +
             '</div>' +
             '<div class="hhci__p">' + esc(money(it.final_line_price)) + was + '</div>' +
           '</div>';
  }

  function renderCart() {
    var body = el("hh-lines"), cnt = el("hh-count"), tot = el("hh-total"),
        save = el("hh-save"), savev = el("hh-savev"), go = el("hh-checkout"),
        err = el("hh-err"), dr = el("hh-drawer");
    if (!body) return;

    if (dr) { if (cartBusy) dr.setAttribute("aria-busy", "true"); else dr.removeAttribute("aria-busy"); }

    if (err) {
      if (cartErr) { err.textContent = cartErr; err.hidden = false; }
      else { err.textContent = ""; err.hidden = true; }
    }

    /* CART === null is NOT an empty cart. Telling a shopper who has three kits in there that
       it is empty is worse than any spinner. */
    if (!CART) {
      body.innerHTML = '<p class="hhcart__load">Loading your cart&hellip; ' +
                       '<button class="hhcart__link" type="button" data-hh-retry>Try again</button></p>';
      if (cnt) cnt.textContent = "";
      paintBadge(0);
      if (tot) tot.textContent = "—";
      if (save) save.hidden = true;
      if (go) { go.setAttribute("disabled", ""); go.setAttribute("aria-disabled", "true"); }
      return;
    }

    var items = (CART.items || []).slice(), kits = [], bmp = [];
    items.forEach(function (it) { (isBumpVariant(it.variant_id) ? bmp : kits).push(it); });
    items = kits.concat(bmp);        /* kits first — a $3.99 add-on must not head the list */

    if (items.length) {
      /* bottle lines (if any) first, then their free shipping-protection row; a cart without bottles is drawn exactly as before */
      var bot = kits.filter(isBottleItem), rest = kits.filter(function (it) { return !isBottleItem(it); });
      body.innerHTML = bot.map(rowHTML).join("") + freeProtRowHTML() + rest.concat(bmp).map(rowHTML).join("");
    } else {
      /* removing the last line leaves the drawer OPEN on this — a drawer that shuts itself
         the moment a shopper removes something reads as the site rejecting them */
      body.innerHTML = '<div class="hhcart__empty">' +
                         '<i class="ms">shopping_bag</i>' +
                         '<p>Your cart is empty.</p>' +
                         '<p class="s">Pick an edition and a pack to get started.</p>' +
                         '<button class="hhcart__link" type="button" data-hh-shop>Choose your kit</button>' +
                       '</div>';
    }

    var n = Number(CART.item_count) || 0;
    /* a Prism set and its Extra-figure lines count as one item, the same as on the Prism page */
    var shown = (CART.items || []).reduce(function (a, it) { return a + (isPrismExtra(it) ? 0 : (Number(it.quantity) || 0)); }, 0);
    if (cnt) cnt.textContent = shown ? " · " + shown + (shown === 1 ? " item" : " items") : "";
    paintBadge(shown);
    if (tot) tot.textContent = money(Number(CART.items_subtotal_price) || 0);

    var disc = (Number(CART.total_discount) || 0) + markdownSavings(CART);
    if (save) {
      if (disc > 0) { if (savev) savev.textContent = "−" + money(disc); save.hidden = false; }
      else save.hidden = true;      /* never a "-$0.00" row */
    }

    if (go) {
      if (n > 0 && !cartBusy) { go.removeAttribute("disabled"); go.removeAttribute("aria-disabled"); }
      else { go.setAttribute("disabled", ""); go.setAttribute("aria-disabled", "true"); }
    }

    renderUpsells();
    renderUrgency();
  }

  /* ---- ADD TO YOUR ORDER -------------------------------------------------------------------
     The two order bumps, offered again inside the drawer. Deliberately only these two: the
     reference cart this was modelled on also cross-sells other SKUs, but this store has one
     product, so anything else would be invented. Checkbox state is read back from the CART, not
     remembered locally, so it cannot drift from what Shopify actually holds. */
  /* Inline SVG, not asset_url: a cart row icon that 404s is worse than no icon, and these ship
     with the script so they cannot get out of sync with a theme asset. currentColor throughout so
     the tiles inherit the row's colour. */
  var ICON_SHIELD =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M12 2.8 4.6 5.9v5.3c0 4.6 3.1 8.4 7.4 9.9 4.3-1.5 7.4-5.3 7.4-9.9V5.9L12 2.8Z"/>' +
    '<path d="m8.9 11.9 2.2 2.2 4.1-4.4"/></svg>';
  var ICON_MEDAL =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="14.6" r="5.4"/><path d="m8.6 9.6 -2.4-6.4h11.6l-2.4 6.4"/>' +
    '<path d="M12 12.2v4.8"/><path d="M10.2 14.6h3.6"/></svg>';

  var UPSELLS = [
    { tag: "protection", name: "Shipping protection", icon: ICON_SHIELD,
      blurb: "Lost, stolen or damaged in transit and we send another one." },
    { tag: "warranty",   name: "Lifetime warranty", icon: ICON_MEDAL,
      blurb: "Snapped wire, cracked bead, dead light \u2014 we replace the part free." }
  ];

  /* ---- Cross-sell: the edition that is NOT in the cart --------------------------------------
     A shopper who has bought one edition is the best possible buyer for the other -- the page's
     own line for it is "The same craft, in a different tray of beads", so that is what this row
     says. Offered as ONE kit regardless of the pack they chose: this is a second-occasion add,
     not an upgrade, and $39.99 is the lowest-friction version of it.

     Shown only when EXACTLY ONE edition is in the cart. Neither (empty cart) and both (already
     taken) both render nothing -- an offer to add what you already have reads broken. */
  var EDITIONS = {
    christmas: { label: "Christmas", colours: "Red, green and gold" },
    halloween: { label: "Halloween", colours: "Orange, purple and black" }
  };

  function editionsInCart() {
    var have = { christmas: false, halloween: false };
    if (!CART || !CART.items) return have;
    for (var i = 0; i < CART.items.length; i++) {
      var t = norm(CART.items[i].variant_title || "");
      if (t.indexOf("christmas") === 0) have.christmas = true;
      else if (t.indexOf("halloween") === 0) have.halloween = true;
    }
    return have;
  }

  /* §14: the other edition's One kit, from whichever kit product carries it — this page's own
     variants first, then HH.kits (so The Halloween Tree Kit page can still offer Christmas). */
  function oneKitFor(ed) {
    var v = lookup(ed, "One kit");
    if (valid(v)) return v;
    var ks = (HH && HH.kits) || {}, ids = Object.keys(ks);
    for (var i = 0; i < ids.length; i++) {
      var k = ks[ids[i]];
      if (k && norm(k.ed) === ed && Number(k.qty) === 1 && valid(k)) return k;
    }
    return null;
  }

  function crossSellHTML() {
    var have = editionsInCart();
    if (have.christmas === have.halloween) return "";        /* none, or both -> nothing to offer */
    var missing = have.christmas ? "halloween" : "christmas";
    var v = oneKitFor(missing);
    if (!valid(v)) return "";                                /* sold out or missing -> ships empty */
    var meta = EDITIONS[missing];
    var own = mediaFor(missing, true);
    var thumb = (own[0] && (own[0].t || own[0].p)) || (HH && HH.edThumb && HH.edThumb[missing]) || "";
    return '<button class="hhx" type="button" data-hh-x="' + missing + '"' +
             (cartBusy ? " disabled" : "") + '>' +
             (thumb ? '<span class="hhx__im"><img src="' + esc(thumb) + '" alt="" loading="lazy"></span>' : "") +
             '<span class="hhx__t">' +
               '<b>Add the ' + meta.label + ' edition</b>' +
               '<small>The same craft, in a different tray of beads. ' + meta.colours + '.</small>' +
             '</span>' +
             '<span class="hhx__p">+' + money(v.price) + '</span>' +
             '<span class="hhx__go"><i class="ms">add</i></span>' +
           '</button>';
  }

  function addEdition(ed) {
    if (cartBusy) return;
    var v = oneKitFor(ed);
    if (!valid(v)) { renderCart(); return; }
    cartBusy = true; cartErr = null; renderCart();
    var R = (HH && HH.routes) || {};
    var done = function () { cartBusy = false; renderCart(); };
    post(api(R.add, "/cart/add.js"), { items: [{ id: Number(v.id), quantity: 1 }] })
      .then(function (res) {
        if (res.ok) return refreshCart();
        return res.json().catch(function () { return null; }).then(function (j) {
          cartErr = (j && (j.description || j.message)) || FAILNOTE;
          return refreshCart().catch(function () { });
        });
      })
      .catch(function () { cartErr = FAILNOTE; })
      .then(done, done);
  }

  function lineKeyForVariant(id) {
    if (!CART || !CART.items) return null;
    for (var i = 0; i < CART.items.length; i++) {
      if (Number(CART.items[i].variant_id) === Number(id)) return CART.items[i].key;
    }
    return null;
  }

  /* ---- Urgency + free-shipping progress ------------------------------------------------------
     Both halves are FACTS, not invented pressure. The countdown is arithmetic on today's date.
     The threshold is the store's real one: the Domestic delivery zone carries a $0.00 Standard
     rate conditional on TOTAL_PRICE >= $70.00, alongside an unconditional $8.00 Standard. So a
     shopper under the threshold genuinely pays $8.00 and genuinely stops paying it at $70. No
     fake stock counts, no fake timers -- those are the things this account has been burned by. */

  function daysUntil(mmdd) {
    if (!mmdd) return null;
    var parts = String(mmdd).split("-");
    if (parts.length !== 2) return null;
    var now = new Date();
    var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var target = new Date(now.getFullYear(), Number(parts[0]) - 1, Number(parts[1]));
    if (target < today) target = new Date(now.getFullYear() + 1, Number(parts[0]) - 1, Number(parts[1]));
    return Math.round((target - today) / 86400000);
  }

  /* Which holiday to count to depends on what is actually IN the cart, not on the picker --
     the shopper may have switched edition after adding. Christmas wins a mixed cart. */
  function cartEdition() {
    if (!CART || !CART.items) return null;
    var sawHw = false;
    for (var i = 0; i < CART.items.length; i++) {
      var t = norm(CART.items[i].variant_title || "");
      if (t.indexOf("christmas") === 0) return "christmas";
      if (t.indexOf("halloween") === 0) sawHw = true;
    }
    return sawHw ? "halloween" : null;
  }

  function renderUrgency() {
    var wrap = el("hh-urg"), t = el("hh-urgt"), sub = el("hh-urgs"),
        fill = el("hh-barfill"), ship = el("hh-ship");
    if (!wrap) return;

    var n = CART ? (Number(CART.item_count) || 0) : 0;
    if (!CART || n === 0) { wrap.hidden = true; if (ship) ship.textContent = "\u2014"; return; }

    var sh = (HH && HH.shipping) || {};
    var thr = Number(sh.threshold) || 0;
    var std = Number(sh.standard);
    if (!isFinite(std)) std = 0;
    var sub_c = Number(CART.items_subtotal_price) || 0;

    /* Two worlds. Either shipping is free outright (standard rate 0 and no threshold), or it is
       free above a threshold and there is progress worth showing. Read live 2026-09-08: the
       Domestic zone now has ONE method, "Express shipping" at $0.00 with no conditions, so we are
       in the first world and there is nothing to progress toward. Do not invent a bar for it. */
    var alwaysFree = std === 0 && thr === 0;
    var free = alwaysFree || (thr > 0 && sub_c >= thr);

    if (ship) {
      ship.textContent = free ? "Free" : money(std);
      ship.className = free ? "is-free" : "";
    }

    /* headline: the PRESEASON SALE (Julian, 2026-09-12 — replaced the "N days until Christmas"
       countdown). Leads with THIS cart's real markdown saving, computed by markdownSavings() from
       live compare-at prices, so the figure always equals the "Preseason savings" row below it.
       If the cart holds only add-ons (no kit, so no saving) it falls back to the floored
       "up to X% off" — the same number the banner and sticker show. daysUntil()/cartEdition()
       and HH.deadlines are no longer read here; left in place, harmless. */
    var saved = markdownSavings(CART), pct = maxOffPct();
    var tag = '<span class="hhcart__saletag"><i class="ms">local_offer</i> Preseason sale</span>';
    if (saved > 0)
      t.innerHTML = tag + '<span class="hhcart__saletxt">You’re saving <b>' + money(saved) +
                    '</b> on this order — ends tonight.</span>';
    else if (pct > 0)
      t.innerHTML = tag + '<span class="hhcart__saletxt">Up to <b>' + pct +
                    '% off</b> every kit — ends tonight.</span>';
    else
      t.innerHTML = tag + '<span class="hhcart__saletxt">Ends tonight.</span>';

    var bar = fill && fill.parentNode;
    if (alwaysFree) {
      if (bar) bar.hidden = true;
      sub.innerHTML = '<i class="ms">check</i> Free shipping included on your order.';
    } else if (thr > 0 && !free) {
      if (bar) bar.hidden = false;
      var left = thr - sub_c;
      fill.style.width = Math.max(4, Math.min(100, (sub_c / thr) * 100)).toFixed(1) + "%";
      fill.className = "";
      sub.innerHTML = "You are <b>" + money(left) + "</b> from free shipping.";
    } else if (thr > 0) {
      if (bar) bar.hidden = false;
      fill.style.width = "100%";
      fill.className = "is-done";
      sub.innerHTML = '<i class="ms">check</i> Free shipping unlocked on this order.';
    } else {
      if (bar) bar.hidden = true;
      sub.textContent = "";
    }
    wrap.hidden = false;
  }

  function renderUpsells() {
    var wrap = el("hh-ups"), list = el("hh-upslist");
    if (!wrap || !list) return;
    var freeProt = freeBottleInCart(), bottlesOnly = onlyBottlesInCart();
    var rows = UPSELLS.map(function (u) {
      var b = bump(u.tag);
      if (!b || !b.id) return "";                 /* bump product missing -> row ships empty */
      if (u.tag === "protection" && freeProt) return "";   /* included free with The Pair / The Family Set of bottles */
      if (u.tag === "warranty" && bottlesOnly) return "";  /* kit wording; never offered for the bottle */
      var on = inCart(b.id);
      return '<label class="hhup' + (on ? " on" : "") + '" data-hh-up="' + u.tag + '">' +
               '<input type="checkbox"' + (on ? " checked" : "") + (cartBusy ? " disabled" : "") + '>' +
               '<span class="hhup__ico">' + u.icon + '</span>' +
               '<span class="hhup__box"><i class="ms">check</i></span>' +
               '<span class="hhup__t"><b>' + u.name + '</b><small>' + u.blurb + '</small></span>' +
               '<span class="hhup__p">+' + money(b.price) + '</span>' +
             '</label>';
    }).join("");
    var cross = crossSellHTML();
    list.innerHTML = cross + rows;
    /* hide the whole block if the cart is empty -- offering an add-on for nothing reads broken */
    var n = CART ? (Number(CART.item_count) || 0) : 0;
    wrap.hidden = (!rows && !cross) || n === 0;
  }

  function toggleUpsell(tag, want) {
    var b = bump(tag);
    if (!b || !b.id || cartBusy) { renderCart(); return; }
    cartBusy = true; cartErr = null; renderCart();
    var R = (HH && HH.routes) || {};
    var done = function () { cartBusy = false; renderCart(); };
    if (want) {
      post(api(R.add, "/cart/add.js"), { items: [{ id: Number(b.id), quantity: 1 }] })
        .then(function (res) {
          if (res.ok) return refreshCart();
          return res.json().catch(function () { return null; }).then(function (j) {
            cartErr = (j && (j.description || j.message)) || FAILNOTE;
            return refreshCart().catch(function () { });
          });
        })
        .catch(function () { cartErr = FAILNOTE; })
        .then(done, done);
    } else {
      var k = lineKeyForVariant(b.id);
      if (!k) { done(); return; }
      cartBusy = false;                 /* changeLine owns the busy flag from here */
      changeLine(k, 0);
    }
  }

  /* v28: the header cart button (layout/heirloom.liquid on the kit pages, heirloom-buybox's homepage header): opens the drawer on a
     fresh /cart.js read, shows the same count as the drawer title, and gets the focus back when the drawer closes. */
  var hhOpener = null;
  function paintBadge(n) {
    n = Number(n) || 0;
    var ns = document.querySelectorAll("[data-hh-cartn]"), bs = document.querySelectorAll("button.cartbtn[data-hh-open]"), i;
    for (i = 0; i < ns.length; i++) { ns[i].hidden = !n; ns[i].textContent = String(n); }
    for (i = 0; i < bs.length; i++) bs[i].setAttribute("aria-label", n ? "Open cart, " + n + (n === 1 ? " item" : " items") : "Open cart");
  }

  function openCart() {
    var dr = el("hh-drawer"), sc = el("hh-scrim"), x = el("hh-close");
    renderCart();                                  /* render BEFORE .on, so it never slides in stale */
    if (sc) sc.classList.add("on");                /* the scrim needs .on too — without it the
                                                      backdrop never dims AND, because it keeps
                                                      pointer-events:none, click-outside can never fire */
    if (dr) { dr.classList.add("on"); dr.setAttribute("aria-hidden", "false"); }
    if (x) { try { x.focus({ preventScroll: true }); } catch (e) { } }
  }

  function closeCart() {
    var dr = el("hh-drawer"), sc = el("hh-scrim");
    if (sc) sc.classList.remove("on");
    if (dr) { dr.classList.remove("on"); dr.setAttribute("aria-hidden", "true"); }
    if (hhOpener) { var o = hhOpener; hhOpener = null; if (document.contains(o)) { try { o.focus({ preventScroll: true }); } catch (e) { } } }
    /* closing writes nothing, clears nothing, and resets nothing */
  }

  function changeLine(k, q) {
    if (cartBusy || !k) return;
    q = Math.round(Number(q));
    if (!isFinite(q)) return;
    q = Math.max(0, Math.min(10, q));

    var R = (HH && HH.routes) || {};
    cartBusy = true;                               /* one write in flight; every other control
                                                      returns immediately, so a double-tap cannot
                                                      write twice against a stale cart */
    cartErr = null;
    renderCart();

    post(api(R.change, "/cart/change.js"), { id: String(k), quantity: q })
      .then(function (res) {
        /* branch on res.ok — the JSON "status" field is the integer 422 in one error body and
           the string "bad_request" in the next */
        if (res.ok) return res.json().then(function (c) { CART = c; });
        return res.json().catch(function () { return null; }).then(function (j) {
          cartErr = (j && (j.description || j.message)) || FAILNOTE;
          /* never keep showing a quantity the server rejected; keep the last good view if
             even the re-read fails, rather than blanking the drawer */
          return refreshCart().catch(function () { });
        });
      })
      .catch(function () { if (!cartErr) cartErr = FAILNOTE; })
      .then(function () { cartBusy = false; renderCart(); });   /* cleared here, always —
                                                                   a stranded guard bricks every
                                                                   control with no visible cause */
  }

  /* An old cart can hold a per-order bump at quantity > 1. Correct it down to 1 — once per
     key per page load, so this can never loop. The add path is what stops it being created. */
  function fixBumpQty() {
    if (cartBusy || !CART || !CART.items) return;
    if (freeBottleInCart()) {                      /* a paid protection next to a Pair / Family Set of bottles: take it out */
      for (var j = 0; j < CART.items.length; j++) {
        if (bumpTagFor(CART.items[j].variant_id) === "protection") { changeLine(CART.items[j].key, 0); return; }
      }
    }
    for (var i = 0; i < CART.items.length; i++) {
      var it = CART.items[i];
      if (isBumpVariant(it.variant_id) && Number(it.quantity) > 1 && !bumpFixed[it.key]) {
        bumpFixed[it.key] = 1;
        changeLine(it.key, 1);
        return;
      }
    }
  }

  function goCheckout() {
    if (cartBusy || !CART || !Number(CART.item_count)) return;
    var b = el("hh-checkout");
    /* innerHTML, never textContent — the <i class="ms"> inside must survive */
    if (b) { b.setAttribute("disabled", ""); b.innerHTML = '<i class="ms">lock</i> Taking you to checkout&hellip;'; }
    /* nothing is rebuilt, re-added or cleared: the cart already IS what the drawer showed,
       because the drawer has been reading it from the server all along */
    location.href = (HH && HH.routes && HH.routes.checkout) || "/checkout";
  }

  /* Delegated on document and bound ONCE. bindable() stamps a node, which is right for a node
     that survives — but the drawer and the sticky bar live in heirloom-story.liquid while
     init() is driven by the buy box, so reloading the story section in the theme editor would
     hand us fresh, unstamped, unbound nodes. */
  function onDocClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest("[data-hh-add]")) { e.preventDefault(); addToCart(false); return; }

    /* The four express buttons. Same add path as the main button -- same variant, same ticked
       add-ons, same price -- then straight to checkout instead of opening the drawer. */
    var buy = t.closest("[data-hh-buy]");
    if (buy) {
      e.preventDefault();
      if (busy) return;
      if (walnote) walnote.textContent = "Taking you to checkout\u2026";
      addToCart(true);
      return;
    }
    var x = t.closest("[data-hh-x]");
    if (x) { e.preventDefault(); addEdition(x.getAttribute("data-hh-x")); return; }

    if (t.closest("#hh-close") || t.closest("#hh-scrim")) { closeCart(); return; }
    var op = t.closest("[data-hh-open]");
    if (op) {
      e.preventDefault();
      if (cartBusy) { openCart(); hhOpener = op; return; }      /* a write is in flight: show the drawer as it stands */
      refreshCart().catch(function () { }).then(function () { openCart(); hhOpener = op; });
      return;
    }
    if (t.closest("#hh-checkout")) { goCheckout(); return; }
    if (t.closest("[data-hh-retry]")) {
      cartErr = null;
      refreshCart().then(renderCart, function () { cartErr = FAILNOTE; renderCart(); });
      return;
    }
    if (t.closest("[data-hh-shop]")) {
      closeCart();
      var box = document.querySelector(".buybox");
      if (box) box.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (t.closest("[data-hh-prism-rm]")) { removePrismSet(); return; }
    var bs = t.closest("[data-hh-bstep]");      /* the bottle stepper: swap the tier (its delete is a plain data-hh-q="0") */
    if (bs) { if (!bs.disabled && !cartBusy) stepBottle(Number(bs.getAttribute("data-hh-bstep")), bs.getAttribute("data-dir")); return; }
    var q = t.closest("[data-hh-q]");
    if (q) {
      var row = q.closest("[data-hh-key]");
      if (row) changeLine(row.getAttribute("data-hh-key"), q.getAttribute("data-hh-q"));
    }
  }

  /* An Extra-figure line with no Prism set beside it would sell one figure at $66.66 (e.g. the set was removed on the
     base theme's /cart page). Take it out on load, the same guard the Prism page runs. */
  function guardPrismOrphans() {
    if (!CART || !CART.items) return;
    var hasSet = CART.items.some(function (i) { return isPrismItem(i) && !isPrismExtra(i); });
    var upd = {}, any = false, freeProt = freeBottleInCart();
    CART.items.forEach(function (it) {
      if (isPrismExtra(it) && !hasSet) { upd[it.key] = 0; any = true; }
      else if (freeProt && bumpTagFor(it.variant_id) === "protection") { upd[it.key] = 0; any = true; }   /* included free with 2-3 bottles */
    });
    if (!any) return;
    return post(api((HH && HH.routes && HH.routes.update) || null, "/cart/update.js"), { updates: upd })
      .then(function (res) { return res.ok ? res.json().then(function (c) { CART = c; }) : refreshCart(); })
      .catch(function () { });
  }

  function removePrismSet() {
    if (cartBusy || !CART || !CART.items) return;
    var upd = {}, any = false;
    CART.items.forEach(function (it) { if (isPrismItem(it)) { upd[it.key] = 0; any = true; } });
    if (!any) return;
    cartBusy = true; cartErr = null; renderCart();
    post(api((HH && HH.routes && HH.routes.update) || null, "/cart/update.js"), { updates: upd })
      .then(function (res) {
        if (res.ok) return res.json().then(function (c) { CART = c; });
        return res.json().catch(function () { return null; }).then(function (j) {
          cartErr = (j && (j.description || j.message)) || FAILNOTE;
          return refreshCart().catch(function () { });
        });
      })
      .catch(function () { if (!cartErr) cartErr = FAILNOTE; })
      .then(function () { cartBusy = false; renderCart(); });
  }

  function onDocChange(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var up = t.closest("[data-hh-up]");
    if (up && t.type === "checkbox") {
      toggleUpsell(up.getAttribute("data-hh-up"), !!t.checked);
      return;
    }

    if (!t.hasAttribute || !t.hasAttribute("data-hh-qty")) return;
    var row = t.closest("[data-hh-key]");
    if (!row) return;
    var q = Math.round(Number(t.value));
    if (!isFinite(q) || q < 1) { renderCart(); return; }     /* revert to the server's number */
    changeLine(row.getAttribute("data-hh-key"), Math.min(10, q));
  }

  function onDocKey(e) {
    if (e.key === "Escape" || e.keyCode === 27) closeCart();
  }

  /* ================= init ================= */
  function init() {
    if (!window.HH) return;                      /* buy box is already rendered disabled (§7) */
    HH = window.HH;

    initMarquee();

    stage = document.getElementById("stage");
    trail = document.getElementById("trail");
    dots = document.getElementById("dots");
    if (!stage || !trail || !dots) return;

    tiers = [].slice.call(document.querySelectorAll("#tiers .tier"));
    eds = [].slice.call(document.querySelectorAll("#editions .ed"));
    edSwaps = [].slice.call(document.querySelectorAll("[data-ed]"));
    bumps = [].slice.call(document.querySelectorAll(".bump"));
    atc = document.getElementById("atc");
    sub = document.getElementById("stickySub");
    walnote = document.getElementById("walnote");
    wallets = document.getElementById("wallets");
    form = document.getElementById("hh-form");
    hiddenId = document.getElementById("hh-variant");

    /* capture the original note ONCE per node, so a re-init while a bump is
       checked cannot record the swapped text as the original (§8) */
    walnoteOrig = "";
    if (walnote) {
      if (!walnote.getAttribute("data-hh-note")) walnote.setAttribute("data-hh-note", walnote.textContent);
      walnoteOrig = walnote.getAttribute("data-hh-note");
    }
    lastEd = null;
    noteMsg = null;                              /* a section reload must not keep a stale failure */

    MEDIA = (HH.media && HH.media.length) ? HH.media : [];
    HERO = { christmas: Number(HH.hero && HH.hero.christmas),
             halloween: Number(HH.hero && HH.hero.halloween) };

    if (bindable(document.getElementById("gPrev"))) document.getElementById("gPrev").addEventListener("click", function () { go(gi - 1) });
    if (bindable(document.getElementById("gNext"))) document.getElementById("gNext").addEventListener("click", function () { go(gi + 1) });

    if (bindable(stage)) {
      var sx = null;
      stage.addEventListener("touchstart", function (e) { sx = e.touches[0].clientX }, { passive: true });
      stage.addEventListener("touchend", function (e) {
        if (sx === null) return;
        var dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 44) go(gi + (dx < 0 ? 1 : -1)); sx = null;
      }, { passive: true });
    }

    tiers.forEach(function (t) { if (bindable(t)) t.addEventListener("change", onChange) });
    bumps.forEach(function (b) { if (bindable(b)) b.addEventListener("change", onChange) });
    eds.forEach(function (e) { if (bindable(e)) e.addEventListener("change", onChange) });

    if (atc && bindable(atc)) {
      atc.addEventListener("click", function (e) { e.preventDefault(); addToCart(); });
    }
    if (form && bindable(form)) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();                      /* a native POST /cart/add can only ever build a wrong cart */
        var s = e.submitter;
        if (!s || s === atc) addToCart();
      });
    }

    buildGallery();   /* must run before sync() — paintEdition only jumps, it no longer builds */
    sync();

    var box = document.querySelector(".buybox"), bar = document.getElementById("sticky");
    if (obs) { obs.disconnect(); obs = null; }
    if (box && bar && "IntersectionObserver" in window) {
      obs = new IntersectionObserver(function (e) {
        bar.classList.toggle("show", !e[0].isIntersecting);
      }, { rootMargin: "-120px 0px 0px 0px" });
      obs.observe(box);
    }

    /* drawer wiring, LAST — nothing below feeds the gallery or the price paint, so a throw
       introduced here can never render a dead buy box */
    if (!drawerBound) {
      drawerBound = true;
      document.addEventListener("click", onDocClick);
      document.addEventListener("change", onDocChange);
      document.addEventListener("keydown", onDocKey);
    }
    closeCart();                                 /* a section reload must not leave it half-open */
    /* read the real cart once: the cart cookie outlives the page, so a returning shopper may
       already have lines, and the add path needs to know which bumps are already in there */
    refreshCart().then(guardPrismOrphans).then(normalizeBottles).then(function () { renderCart(); }, function () { /* leave CART null — the
                                       drawer shows its retry line rather than a false "empty" */ });
  }

  init();
  document.addEventListener("shopify:section:load", init);

})();
